// src/index.tsx

// This is the main entry point for the site. It is responsible for rendering the site and setting up the theme, context, and authentication systems.

// React Imports
import React from "react";
import ReactDOM from "react-dom/client";
// Material UI Imports
import { ThemeProvider, CssBaseline } from "@mui/material";
// Theme Imports
import theme from "theme/theme";
// Authentication Imports
import Authenticate from "helpers/Authenticate";
import { Authenticator } from "@aws-amplify/ui-react";
import { Amplify } from "aws-amplify";
import config from "./amplifyconfiguration.json";
// CSS Imports
import "./index.css";
// Datadog Imports
import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import { DebugLevel, SQW_Log } from "services/Logging";

Amplify.configure(config);

// Check if Datadog can connect before initializing
const checkDatadogConnection = async () => {
    try {
        // Try to fetch from Datadog API to check connectivity
        const response = await fetch('https://browser-intake-datadoghq.com/api/v2/rum?ddsource=browser&ddtags=sdk_version%3A5.20.0%2Capi%3Axhr%2Cenv%3Adev%2Cservice%3Aservquest-web%2Cversion%3A1.1.0', {
            method: 'HEAD',
            mode: 'no-cors',
            cache: 'no-cache',
        });
        // If we get a response (even an error), Datadog is reachable
        return response.ok;
    } catch (error) {
        SQW_Log(DebugLevel.WARN, "Datadog connection check failed: ", error);
        return false;
    }
};

checkDatadogConnection().then((canConnect) => {
    if (canConnect) {
        try{
            // Datadog is reachable, initialize it
            datadogRum.init({
                applicationId: "dcc4d5d7-cb43-4a47-ae23-7b89222eb2ed",
                clientToken: "pub5d98f056bf26a990739ef9ce5aeb1272",
                site: "datadoghq.com",
                service: "servquest-web",
                env: "dev",
                version: "1.1.0", // Specify a version number to identify the deployed version of your application in Datadog.
                sessionSampleRate: 100,
                sessionReplaySampleRate: 20,
                trackUserInteractions: true,
                trackResources: true,
                trackLongTasks: true,
                defaultPrivacyLevel: "mask-user-input",
            });
            SQW_Log(DebugLevel.INFO, "Datadog initialized successfully.");
            datadogRum.startSessionReplayRecording(); // Have datadog record sessions for playback.

            datadogLogs.init({
                clientToken: "pub5d98f056bf26a990739ef9ce5aeb1272",
                site: "datadoghq.com",
                forwardErrorsToLogs: true,
                service: "servquest-web",
                env: "dev",
                sessionSampleRate: 100,
            });

            datadogLogs.logger.info("Datadog has started logging! Watch out! 🐶");
        }
        catch(error){
            SQW_Log(DebugLevel.ERROR, "Error initializing Datadog: ", error);
        }
    } else {
        // Datadog is not reachable, skip initialization
        SQW_Log(DebugLevel.WARN, "Datadog not reachable, skipping initialization");
    }
});


// Our types for the site data
export interface Props {
    name: String;
    description: String;
    subdomain: String;
    customDomain: String;
}

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

root.render(
    <React.StrictMode>
        <ThemeProvider theme={theme}>
            <CssBaseline>
                <Authenticator.Provider>
                    <Authenticate />
                </Authenticator.Provider>
            </CssBaseline>
        </ThemeProvider>
    </React.StrictMode>,
);
